import { AlertTriangle } from 'lucide-react'

interface EarlyFixtureInfo {
  homeTeam: string
  awayTeam: string
  kickoffTime: Date
}

interface DeadlineBannerProps {
  matchdayNumber: number | undefined
  season: string
  deadlineDate?: Date | null
  isCompleted?: boolean
  countdown: {
    formatted: string
    isExpired: boolean
    hasDeadline: boolean
    totalSeconds: number
  }
  earlyFixture?: EarlyFixtureInfo | null
}

function formatDeadlineDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatTimeUntil(date: Date): string {
  const diffMs = date.getTime() - Date.now()
  if (diffMs <= 0) return 'now'
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

export function DeadlineBanner({ matchdayNumber, season, deadlineDate, isCompleted, countdown, earlyFixture }: DeadlineBannerProps) {
  // Urgency color: green > 24h, amber > 1h, red < 1h, pulsing red when expired
  const urgencyColor = countdown.isExpired
    ? 'text-red-600 dark:text-red-400 animate-pulse'
    : countdown.totalSeconds < 3600
      ? 'text-red-600 dark:text-red-400'
      : countdown.totalSeconds < 86400
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-emerald-600 dark:text-emerald-400'

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
            Matchday {matchdayNumber ?? '...'}
          </h1>
          <span className="text-sm text-zinc-500 dark:text-zinc-400">{season}</span>
        </div>
        <div className="text-right">
          {countdown.hasDeadline ? (
            countdown.isExpired ? (
              <div>
                <span className={`text-sm font-semibold ${isCompleted ? 'text-zinc-500 dark:text-zinc-400' : 'text-red-600 dark:text-red-400 animate-pulse'}`}>
                  {isCompleted ? 'Completed' : 'Deadline passed!'}
                </span>
                {deadlineDate && (
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">
                    {formatDeadlineDate(deadlineDate)}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <span className={`text-lg font-mono font-bold ${urgencyColor}`}>
                  {countdown.formatted}
                </span>
                {deadlineDate && (
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">
                    {formatDeadlineDate(deadlineDate)}
                  </p>
                )}
              </div>
            )
          ) : (
            <span className="text-sm text-zinc-400 dark:text-zinc-500">TBD</span>
          )}
        </div>
      </div>

      {/* Early fixture alert */}
      {earlyFixture && earlyFixture.kickoffTime > new Date() && (
        <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            <span className="font-semibold">Early fixture:</span>{' '}
            {earlyFixture.homeTeam} vs {earlyFixture.awayTeam} kicks off in{' '}
            {formatTimeUntil(earlyFixture.kickoffTime)} — pick before then!
          </p>
        </div>
      )}
    </div>
  )
}
